import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '../../api/client'
import type { MedMappa, MedMappaArco, MedMappaNodo } from '../../api/types'
import { formatBytes } from '../../attributes'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { ErrorBanner, Spinner } from '../common'
import Mappa, { type ArcoMappa, type GruppoNodo, type NodoMappa, type VoceLegenda } from '../Mappa'
import { leggiLayout, scriviLayout, type LayoutMappa } from '../mappaLayout'

/**
 * The clinical map (CENSO MED): electromedical devices, clinical systems and
 * who talks to whom — from the flows (7 days) and from the CENSO agents on the
 * clinical servers (their conversations). Addresses, ports and programs only.
 *
 * Without `focus` the whole area (systems and the devices that talk to them,
 * grouped by model when they are many); with `focus` («device:<id>»,
 * «system:<key>», «ci:<id>») the node and its neighbours. Same canvas as the
 * correlation map (`Mappa`).
 */

const COLORE_PROTOCOLLO: Record<string, string> = {
  DICOM: 'var(--accent)',
  HL7: 'var(--info)',
  other: 'var(--text-muted)',
}

function etichettaProtocollo(p: string): string {
  return p === 'other' ? t('Altro traffico') : p
}

function etichettaFonti(f: string[]): string {
  return f.map((x) => (x === 'agent' ? t('agente CENSO') : x === 'hl7_name' ? t('stesso nome HL7') : t('flussi'))).join(' + ')
}

type FiltroProtocollo = 'all' | 'DICOM' | 'HL7' | 'other'

function livelloDi(n: MedMappaNodo): number {
  return n.kind === 'device' || n.kind === 'group' ? 0 : n.kind === 'system' ? 1 : 2
}

function gruppoDi(n: MedMappaNodo): GruppoNodo {
  if (n.kind === 'device') return 'medicale'
  if (n.kind === 'group') return 'cluster'
  if (n.kind === 'system') return 'server'
  return n.ci_id ? 'server' : 'sconosciuto'
}

function coloreDi(n: MedMappaNodo): string {
  if (n.kind === 'device' || n.kind === 'group') return 'var(--ok)'
  if (n.kind === 'system') return 'var(--info)'
  return 'var(--text-muted)'
}

function sottoDi(n: MedMappaNodo): string | null {
  if (n.kind === 'group') return t('{n} elettromedicali', { n: n.count ?? 0 })
  if (n.kind === 'device') return [n.vendor, n.model].filter(Boolean).join(' ') || null
  if (n.kind === 'system') return n.application || (n.addresses ?? [])[0] || null
  return n.address ?? null
}

export function MappaClinica({ focus, altezza = 520, onApriSistema, iniziale }: {
  focus?: string
  altezza?: number
  /** The map already loaded by the page (it decided whether to show it). */
  iniziale?: MedMappa
  /** Opens a clinical system's window; without it the system opens in «Sistemi clinici». */
  onApriSistema?: (key: string) => void
}) {
  const navigate = useNavigate()
  const dati = useAsync<MedMappa | null>(
    () => (iniziale
      ? Promise.resolve(iniziale)
      : api.medMap(focus).catch((e: { status?: number }) => (e?.status === 404 ? null : Promise.reject(e)))),
    [focus, iniziale],
  )
  const [layout, setLayout] = useState<LayoutMappa>(leggiLayout())
  const [scelto, setScelto] = useState<string | null>(null)
  // One protocol at a time: the DICOM map (modalities → PACS), the HL7 map
  // (analysers → middleware → LIS, with the links from the shared HL7 names).
  const [protocollo, setProtocollo] = useState<FiltroProtocollo>('all')
  const tutta = dati.data
  const m = useMemo<MedMappa | null>(() => {
    if (!tutta || protocollo === 'all') return tutta
    const edges = tutta.edges.filter((a) => a.protocol === protocollo)
    const usati = new Set(edges.flatMap((a) => [a.src, a.dst]))
    if (tutta.focus) usati.add(tutta.focus)
    return { ...tutta, edges, nodes: tutta.nodes.filter((n) => usati.has(n.key)) }
  }, [tutta, protocollo])
  const perChiave = useMemo(() => new Map((m?.nodes ?? []).map((n) => [n.key, n])), [m])

  const nodi = useMemo<NodoMappa[]>(
    () => (m?.nodes ?? []).map((n) => ({
      id: n.key,
      label: n.kind === 'group' ? `${n.name} ×${n.count ?? 0}` : n.name,
      sotto: sottoDi(n),
      colore: coloreDi(n),
      gruppo: gruppoDi(n),
      livello: livelloDi(n),
      seme: m?.focus === n.key,
    })),
    [m],
  )
  const archi = useMemo<ArcoMappa[]>(() => {
    const massimo = Math.max(1, ...(m?.edges ?? []).map((a) => a.bytes + a.samples * 1000))
    return (m?.edges ?? []).map((a: MedMappaArco) => {
      const peso = a.bytes + a.samples * 1000
      const da = perChiave.get(a.src)?.name ?? a.src
      const a_ = perChiave.get(a.dst)?.name ?? a.dst
      const quanto = [a.bytes ? formatBytes(a.bytes) : '', a.samples ? t('{n} letture', { n: a.samples }) : '']
        .filter(Boolean).join(' · ')
      if (a.sources.includes('hl7_name')) {
        return {
          id: `${a.src}>${a.dst}|${a.name ?? ''}`,
          src: a.src,
          dst: a.dst,
          colore: COLORE_PROTOCOLLO.HL7,
          tratteggio: '1.5 3',
          spessore: 1,
          etichetta: `HL7 «${a.name ?? ''}»`,
          titolo: t('{a} e {b} usano lo stesso nome HL7 «{nome}»: un’ipotesi dai nomi, non traffico osservato.', { a: da, b: a_, nome: a.name ?? '' }),
        }
      }
      return {
        id: `${a.src}>${a.dst}|${a.protocol}`,
        src: a.src,
        dst: a.dst,
        colore: COLORE_PROTOCOLLO[a.protocol] ?? COLORE_PROTOCOLLO.other,
        // Seen only by the agent: dashed, so the two sources read apart.
        tratteggio: a.sources.length === 1 && a.sources[0] === 'agent' ? '5 3' : undefined,
        spessore: 1 + Math.round((4 * Math.log1p(peso)) / Math.log1p(massimo)),
        etichetta: `${etichettaProtocollo(a.protocol)}${a.ports.length ? ` ${a.ports.join(',')}` : ''}`,
        titolo: `${da} → ${a_} · ${etichettaProtocollo(a.protocol)} ${a.ports.join(', ')} · ${etichettaFonti(a.sources)}${quanto ? ` · ${quanto}` : ''}`,
      }
    })
  }, [m, perChiave])

  const legenda: VoceLegenda[] = [
    { etichetta: 'DICOM', colore: COLORE_PROTOCOLLO.DICOM },
    { etichetta: 'HL7', colore: COLORE_PROTOCOLLO.HL7 },
    { etichetta: t('Altro traffico'), colore: COLORE_PROTOCOLLO.other },
    { etichetta: t('visto solo dall’agente CENSO'), colore: 'var(--text-muted)', tratteggio: '5 3' },
    { etichetta: t('stesso nome HL7 (ipotesi)'), colore: COLORE_PROTOCOLLO.HL7, tratteggio: '1.5 3' },
    { etichetta: t('Elettromedicale'), colore: 'var(--ok)', forma: 'punto' },
    { etichetta: t('Sistema clinico'), colore: 'var(--info)', forma: 'punto' },
  ]

  if (dati.loading && !tutta) return <Spinner />
  if (dati.error) return <ErrorBanner error={dati.error} />
  if (!tutta || !m) return null
  if (tutta.nodes.length === 0 || (focus && tutta.edges.length === 0)) {
    return (
      <p className="field__hint">
        {t('Nessun collegamento clinico ancora: servono i flussi (NetFlow/IPFIX) o l’agente CENSO sui sistemi clinici.')}
      </p>
    )
  }
  const conta = (p: FiltroProtocollo) => (p === 'all' ? tutta.edges.length : tutta.edges.filter((a) => a.protocol === p).length)
  const filtri: [FiltroProtocollo, string][] = [['all', t('Tutti i protocolli')], ['DICOM', 'DICOM'], ['HL7', 'HL7'], ['other', t('Altro traffico')]]

  function apri(id: string) {
    const n = perChiave.get(id)
    if (!n) return
    if (n.kind === 'device' && n.device_id) navigate(`/medical/devices/${n.device_id}`)
    else if (n.kind === 'system' && n.system_key) {
      if (onApriSistema) onApriSistema(n.system_key)
      else navigate(`/medical/systems?apri=${encodeURIComponent(n.system_key)}`)
    }
  }

  return (
    <>
      <div className="chips" role="group" aria-label={t('Protocollo')} style={{ marginBottom: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {filtri.map(([k, etichetta]) => (
          <button key={k} type="button" className={protocollo === k ? 'chip chip--active' : 'chip'}
                  aria-pressed={protocollo === k} disabled={k !== 'all' && conta(k) === 0}
                  onClick={() => setProtocollo(k)}>
            {etichetta} <small className="text-muted">{conta(k)}</small>
          </button>
        ))}
      </div>
      {protocollo === 'HL7' && (
        <p className="field__hint">
          {t('In HL7 si legano anche i sistemi che usano lo stesso nome di applicazione o di struttura (per esempio il server «ALINITY» e gli analizzatori «ALINITY1», «ALINITY2»): linea a puntini, un’ipotesi dai nomi delle intestazioni, mai dal contenuto dei messaggi.')}
        </p>
      )}
      {(m.grouped || m.hidden_others > 0) && (
        <p className="field__hint">
          {m.grouped && t('Molti elettromedicali: sono raggruppati per modello. Aprendo un sistema si vedono uno per uno.')}
          {m.hidden_others > 0 && ` ${t('Altri {n} interlocutori meno attivi non sono disegnati.', { n: m.hidden_others })}`}
        </p>
      )}
      <Mappa
        nodi={nodi}
        archi={archi}
        layout={layout}
        onLayout={(l) => {
          setLayout(l)
          scriviLayout(l)
        }}
        nomiFasce={(l) => (l === 0 ? t('Elettromedicali') : l === 1 ? t('Sistemi clinici') : t('Altri interlocutori'))}
        selezionato={scelto}
        onClickNodo={(id) => setScelto((s) => (s === id ? null : id))}
        onDoppioClickNodo={apri}
        onClickSfondo={() => setScelto(null)}
        legenda={legenda}
        altezza={altezza}
        etichetta={t('Mappa clinica')}
      />
      {scelto && perChiave.get(scelto) && perChiave.get(scelto)!.kind !== 'other' && perChiave.get(scelto)!.kind !== 'group' && (
        <div className="rowactions" style={{ textAlign: 'left', marginTop: 8 }}>
          <button type="button" className="secondary" onClick={() => apri(scelto)}>
            {perChiave.get(scelto)!.kind === 'device' ? t('Apri la scheda del dispositivo') : t('Apri il sistema clinico')}
          </button>
        </div>
      )}
    </>
  )
}
